<template>
  <div
    v-click-outside="switchOffWorkMode"
    class="cameras-finder"
    :class="{ 'cameras-finder_active': showResults }"
  >
    <div class="search-form-input">
      <svg class="icon">
        <use xlink:href="#icon-search" />
      </svg>
      <input
        v-model="newValue"
        class="search-form-input__input"
        :class="inputPaddingClass"
        :placeholder="$t('search')"
        type="search"
        autocomplete="off"
        @input="onInput"
        @keydown.down="down"
        @keydown.up="up"
        @keydown.enter.prevent="handleEnterKey"
        @keydown.esc="handleEscapeKey"
        @focus="onFocus"
      >

      <button
        v-if="showResults && totalCount > 0"
        class="search-form-input__show-all btn-blue"
        type="button"
        @click="showAllResults"
      >
        <svg class="icon icon-arrow-enter">
          <use xlink:href="#icon-arrow-enter" />
        </svg>
        <span class="search-form-input__show-all-text">
          {{ $t('showAll') }} <small>{{ $t('found') }} {{ totalCount }}</small>
        </span>
      </button>
    </div>

    <div v-show="showResults" class="cameras-finder-results">
      <div v-if="isLoading" class="cameras-finder-results__loading">
        <SpinnerLoading />
      </div>
      <template v-else-if="listCamerasInfo.length">
        <div class="cameras-finder-results__headers">
          <div>{{ $t('addresses') }}</div>
          <div>{{ $t('titles') }}</div>
        </div>

        <div
          v-for="(camera, index) in listCamerasInfo"
          :key="camera.number"
          class="cameras-finder-results__item"
          :class="{ active: indexActiveItem === index }"
          @click="selectCamera(camera)"
          @mouseenter="indexActiveItem = index"
          @mouseleave="indexActiveItem = -1"
        >
          <div class="cameras-finder-results__text" v-html="highlightText(camera.address)" />
          <div class="cameras-finder-results__text" v-html="highlightText(camera.title)" />
          <button
            v-if="!selectableOnlyMode"
            :class="camera.isInactive() ? 'btn-gray' : 'btn-blue'"
            class="cameras-finder-results__play"
            type="button"
          >
            <svg class="icon icon-circle-play">
              <use xlink:href="#icon-circle-play" />
            </svg>
          </button>
        </div>
      </template>
      <p v-else class="cameras-finder-results__no-results">
        {{ $t('noResults') }}
      </p>
    </div>
  </div>
</template>

<script>
import {PAGE_SIZES, PARAMS_SORT_CAMERAS, VENDOR_IS_UFANET} from "@/utils/consts.js";
import {
  ACTION_LOAD_CAMERAS,
  ACTION_SEARCH_CAMERAS,
  FIELDS_CAMERA,
} from "@/store/cameras/index.js";
import {YM_GOAL_PLAY_VIA_SEARCH} from "@/utils/tracking.js";
import {QUERY_KEY_ONE_SCREEN_CAMERA_NUMBER} from "@/router/queryKeys.js";

/**
 * Минимальная длинна поисковой строки для которой будет выполняться поиск.
 */
export const CAMS_SEARCH_MIN_LENGTH = 1;

/**
 * Компонент поиска камер.
 *
 * Можно передать начальный текст для поиска, это значение отслеживается в поле ввода.
 * Результаты поиска отображаются в выпадающем списке,
 * в котором имеются интерактивные элементы взаимодействия с каждым результатом.
 */
export default {
  name: "CamerasFinder",
  props: {
    value: {type: String, default: ""},
    selectableOnlyMode: {type: Boolean, default: false},
    allowedCameraNumbers: {type: Array, default: null},
    allowedCameras: {type: Array, default: null},
  },
  data() {
    return {
      newValue: this.value,
      listCamerasInfo: [],
      totalCount: 0,
      showResults: false,
      isLoading: false,
      indexActiveItem: -1,
    };
  },
  computed: {
    inputPaddingClass() {
      if (this.showResults && this.totalCount > 0) {
        return 'with-clear-and-button';
      } else {
        return 'with-clear-only';
      }
    },
  },
  watch: {
    /**
     * Наблюдение за оригинальным значением для его синхронизации со внутренним значением компонента и отправкой события change.
     *
     * @param {String} newVal
     */
    value(newVal) {
      this.newValue = newVal;
    },
  },
  /**
   * Регистрируется вариант функции `loadCameras` с отложенным выполнением.
   */
  created() {
    this.debouncedLoadCameras = _.debounce(this.loadCameras, 400);
  },
  methods: {
    onInput() {
      this.$emit("input", this.newValue);
      if (this.newValue.length >= CAMS_SEARCH_MIN_LENGTH) {
        this.showResults = true;
        this.isLoading = true;
        this.debouncedLoadCameras();
      } else {
        this.resetSearch();
      }
    },
    clearInput() {
      this.newValue = "";
      this.$emit("input", "");
      this.resetSearch();
    },
    resetSearch() {
      this.listCamerasInfo = [];
      this.totalCount = 0;
      this.showResults = false;
      this.indexActiveItem = -1;
    },
    switchOffWorkMode() {
      this.resetSearch();
    },
    /**
     * Функция загрузки камер по заданным опциям поиска.
     */
    async loadCameras() {
      if (!this.newValue || this.newValue.length < CAMS_SEARCH_MIN_LENGTH) return;

      const fields = [
        FIELDS_CAMERA.number,
        FIELDS_CAMERA.address,
        FIELDS_CAMERA.title,
        FIELDS_CAMERA.latitude,
        FIELDS_CAMERA.longitude,
        FIELDS_CAMERA.analytics,
        FIELDS_CAMERA.is_fav,
        FIELDS_CAMERA.is_public,
        FIELDS_CAMERA.inactivity_period,
        FIELDS_CAMERA.permission,
      ];
      let list;
      let total;

      if (Array.isArray(this.allowedCameraNumbers)) {
        const allowedCameraNumbers = [...new Set(
          this.allowedCameraNumbers.map(String).filter(Boolean)
        )];
        const cameras = Array.isArray(this.allowedCameras)
          ? this.allowedCameras
          : allowedCameraNumbers.length
            ? await this.$store.dispatch(`cameras/${ACTION_LOAD_CAMERAS}`, {
              fields,
              numbers: allowedCameraNumbers,
              orderBy: PARAMS_SORT_CAMERAS.addr_asc,
            })
            : [];
        const query = this.newValue.toLowerCase();
        const matchedCameras = cameras.filter(camera => (
          [camera.number, camera.address, camera.title]
            .some(value => String(value || "").toLowerCase().includes(query))
        ));

        total = matchedCameras.length;
        list = matchedCameras.slice(0, PAGE_SIZES.LIMITED);
      } else {
        [list, , total] = await this.$store.dispatch(`cameras/${ACTION_SEARCH_CAMERAS}`, {
          pageSize: PAGE_SIZES.LIMITED,
          page: 1,
          fields,
          query: this.newValue,
          orderBy: PARAMS_SORT_CAMERAS.addr_asc,
        });
      }

      this.listCamerasInfo = list;
      this.totalCount = total;
      this.isLoading = false;
    },
    /**
     * Метод инициирует событие для родительского компонента `show-all-results`,
     * которое обозначает необходимость отображения полного результата поиска.
     */
    showAllResults() {
      this.$emit("show-all-results");
      this.resetSearch();
    },
    /**
     * Клик по текущей камере с целью выбора. Инициирует событие `select-camera` - передачи номера камеры в родительский компонент.
     * В зависимости от режима работы компонента может сразу запустить воспроизведение видео.
     *
     * @param {CameraInfo} cameraInfo
     */
    selectCamera(cameraInfo) {
      this.$emit("select-camera", cameraInfo.number);
      this.play(cameraInfo);
      this.resetSearch();
    },
    /**
     * Воспроизведение прямой трансляции.
     *
     * @param {CameraInfo} cameraInfo
     */
    play(cameraInfo) {
      if (this.selectableOnlyMode) {
        return;
      }
      VENDOR_IS_UFANET && this.$metrika?.reachGoal(YM_GOAL_PLAY_VIA_SEARCH);
      this.$router.push({
        name: this.$route.name,
        query: {
          ...(this.$route.query || {}),
          [QUERY_KEY_ONE_SCREEN_CAMERA_NUMBER]: cameraInfo.number
        }
      });
      this.switchOffWorkMode();
    },
    /**
     * Нажатие клавиши `enter` в поисковой строке.
     *
     * В случае наличия активной строки будет восприниматься как выбор камеры,
     * в противном случае необходимо показать все результаты поиска.
     */
    handleEnterKey() {
      if (this.indexActiveItem >= 0) {
        this.selectCamera(this.listCamerasInfo[this.indexActiveItem]);
      } else if (this.listCamerasInfo.length) {
        this.showAllResults();
      }
    },
    onFocus() {
      if (this.newValue.length >= CAMS_SEARCH_MIN_LENGTH) {
        this.showResults = true;
        this.isLoading = true;
        this.loadCameras();
      }
    },
    handleEscapeKey() {
      this.clearInput();
    },
    /**
     * Нажатие клавиши `down` на поле ввода устанавливает следующий элемент результата как активный.
     * В случае нажатия в не пустой строке поиска, но без данных результатов, произойдет выполнение поискового запроса.
     */
    down() {
      if (!this.showResults || !this.listCamerasInfo.length) return;
      this.indexActiveItem = (this.indexActiveItem + 1) % this.listCamerasInfo.length;
    },
    /**
     * Нажатие клавиши `up` на поле ввода устанавливает предыдущий элемент результата как активный.
     */
    up() {
      if (!this.showResults || !this.listCamerasInfo.length) return;
      this.indexActiveItem =
        this.indexActiveItem <= 0
          ? this.listCamerasInfo.length - 1
          : this.indexActiveItem - 1;
    },
    escapeHtml(s) {
      return String(s)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;")
        .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
    escapeRegExp(s) {
      return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    /**
     * Подсвечивание подстрок поиска в результатах.
     */
    highlightText(text) {
      if (!this.newValue) return this.escapeHtml(text);
      const tokens = this.newValue.split(/\s+/).filter(Boolean).map(this.escapeRegExp);
      if (!tokens.length) return this.escapeHtml(text);
      const re = new RegExp(`(${tokens.join("|")})`, "ig");
      const safe = this.escapeHtml(text);
      return safe.replace(re, "<mark>$1</mark>");
    },
  },
};
</script>

<style lang="scss">
@use "./scss/cameras-finder.scss";
</style>
